import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { AccountInfo } from "@azure/msal-browser";
import { LinearProgress } from "@mui/joy";
import { Core } from "../../utilities/core";
import { CaseItem, Order, PaginatedResponse, FilterOptions, PaginationOptions } from "./types";
import { TicketModal } from "./TicketModal";
import { TicketFilters } from "./TicketFilters";
import { TicketTableView } from "./TicketTableView";
import { RoleTabs } from "./RoleTabs";

export default function TicketTable({ account, instance }: any) {
  const [mounted, setMounted] = React.useState<boolean>(false);

  const [order, setOrder] = React.useState<Order>("desc");
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState<CaseItem[]>();
  const [core, setCore] = React.useState<Core>();

  const [modalId, setModalId] = React.useState<string>();
  const [showModal, setShowModal] = React.useState<boolean>(false);

  // New state for pagination and filtering
  const [loading, setLoading] = React.useState<boolean>(false);
  const [paginationData, setPaginationData] = React.useState<{
    totalSize: number;
    hasMore: boolean;
    limit: number;
    offset: number;
  }>({
    totalSize: 0,
    hasMore: false,
    limit: 25,
    offset: 0,
  });

  const [filters, setFilters] = React.useState<FilterOptions>({
    role: "owner",
  });
  const [searchQuery, setSearchQuery] = React.useState<string>("");

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (mounted && account && instance) {
      new Core().initialiseCore(instance, account).then((core) => {
        setCore(core);
      });
    }
  }, [mounted, account, instance]);

  React.useEffect(() => {
    if (!showModal) {
      setModalId(undefined);
    }
  }, [showModal]);

  // Function to fetch tickets with pagination and filters
  const fetchTickets = React.useCallback(async (pagination: PaginationOptions, currentFilters: FilterOptions, search: string = "") => {
    if (!core || !account) return;

    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("upn", (account as AccountInfo).username);
      queryParams.append("limit", pagination.limit.toString());
      queryParams.append("offset", pagination.offset.toString());

      if (currentFilters.role) queryParams.append("role", currentFilters.role);
      if (currentFilters.status) queryParams.append("status", currentFilters.status);
      if (currentFilters.priority) queryParams.append("priority", currentFilters.priority);
      if (search.trim()) queryParams.append("subject", search.trim());

      const response = await core.invokeIntegrationHubRequest("GET", `/salesforce/tickets?${queryParams.toString()}`);
      const paginatedResponse = response.body as PaginatedResponse<CaseItem>;

      setRows(paginatedResponse.records);
      setPaginationData({
        totalSize: paginatedResponse.totalSize,
        hasMore: paginatedResponse.hasMore,
        limit: paginatedResponse.limit,
        offset: paginatedResponse.offset,
      });
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  }, [core, account]);

  // Initial load
  React.useEffect(() => {
    if (account && core && mounted) {
      fetchTickets(paginationData, filters, searchQuery);
    }
  }, [account, core, mounted]);

  // Refetch when filters change (not search query - that's handled separately)
  React.useEffect(() => {
    if (account && core && mounted) {
      const newPagination = { ...paginationData, offset: 0 }; // Reset to first page when filtering
      setPaginationData(newPagination);
      fetchTickets(newPagination, filters, searchQuery); // Use current searchQuery
    }
  }, [filters, account, core, mounted]);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (paginationData.offset > 0) {
      const newPagination = {
        ...paginationData,
        offset: Math.max(0, paginationData.offset - paginationData.limit),
      };
      setPaginationData(newPagination);
      fetchTickets(newPagination, filters, searchQuery);
    }
  };

  const handleNextPage = () => {
    if (paginationData.hasMore) {
      const newPagination = {
        ...paginationData,
        offset: paginationData.offset + paginationData.limit,
      };
      setPaginationData(newPagination);
      fetchTickets(newPagination, filters, searchQuery);
    }
  };

  // Filter handlers
  const handleFilterChange = (filterType: keyof FilterOptions, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value || undefined,
    }));
  };

  const handleRoleChange = (role: "owner" | "cc") => {
    setFilters((prev) => ({
      ...prev,
      role,
    }));
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleRowClick = (ticketId: string) => {
    setModalId(ticketId);
    setShowModal(true);
  };

  // Debounced search
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (account && core) {
        const newPagination = { ...paginationData, offset: 0 };
        setPaginationData(newPagination);
        fetchTickets(newPagination, filters, searchQuery);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <React.Fragment>
      <TicketModal account={account} instance={instance} showModal={showModal} setShowModal={setShowModal} caseItem={rows?.find((r) => r.Id === modalId)} />

      {/* Role Tabs for mobile */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <RoleTabs
          role={filters.role || "owner"}
          onRoleChange={handleRoleChange}
          size="sm"
        />
      </Box>

      <Sheet className="SearchAndFilters-mobile" sx={{ display: { xs: "flex", sm: "none" }, my: 1, gap: 1 }}>
        <Input 
          size="sm" 
          placeholder="Search by subject" 
          startDecorator={<SearchIcon />} 
          sx={{ flexGrow: 1 }}
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <IconButton size="sm" variant="outlined" color="neutral" onClick={() => setOpen(true)}>
          <FilterAltIcon />
        </IconButton>

        <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
            <ModalClose />
            <Typography id="filter-modal" level="h2">
              Filters
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Sheet sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TicketFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                size="sm"
                showLabels={true}
                direction="column"
              />
              <Button color="primary" onClick={() => setOpen(false)}>
                Apply Filters
              </Button>
            </Sheet>
          </ModalDialog>
        </Modal>
      </Sheet>

      {/* Role Tabs for desktop */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <RoleTabs
          role={filters.role || "owner"}
          onRoleChange={handleRoleChange}
          size="md"
        />
      </Box>

      <Box
        className="SearchAndFilters-tabletUp"
        sx={{
          borderRadius: "sm",
          py: 2,
          display: { xs: "none", sm: "flex" },
          flexWrap: "wrap",
          gap: 1.5,
          "& > *": {
            minWidth: { xs: "120px", md: "160px" },
          },
        }}
      >
        <FormControl sx={{ flex: 1 }} size="sm">
          <FormLabel>Search by subject</FormLabel>
          <Input 
            size="sm" 
            placeholder="Search tickets" 
            startDecorator={<SearchIcon />}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </FormControl>
        <TicketFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          size="sm"
          showLabels={true}
          direction="row"
        />
      </Box>
      {rows !== undefined ? (
        <TicketTableView
          rows={rows}
          selected={selected}
          setSelected={setSelected}
          order={order}
          setOrder={setOrder}
          onRowClick={handleRowClick}
          loading={loading}
        />
      ) : (
        <Box>
          <Typography mb={2} level="body-sm">
            Loading, please wait...
          </Typography>
          <LinearProgress variant="plain" />
        </Box>
      )}
      
      {/* Pagination Controls */}
      {rows && paginationData.totalSize > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            px: 2,
            py: 1,
          }}
        >
          <Typography level="body-sm">
            Showing {paginationData.offset + 1}-{Math.min(paginationData.offset + paginationData.limit, paginationData.totalSize)} of {paginationData.totalSize} tickets
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={paginationData.offset === 0 || loading}
              onClick={handlePreviousPage}
              startDecorator={<NavigateBeforeIcon />}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={!paginationData.hasMore || loading}
              onClick={handleNextPage}
              endDecorator={<NavigateNextIcon />}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
      
      {/* Loading overlay for pagination */}
      {loading && rows && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="plain" size="sm" />
        </Box>
      )}
    </React.Fragment>
  );
}
